import { CreateTagButton } from "../tags/CreateTagButton";
import { PhoneInput } from "../misc/PhoneInput";
import {
  email,
  required,
  useGetList,
  useNotify,
  useRecordContext,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";

import type { ClipboardEventHandler, FocusEvent } from "react";

import { useFormContext } from "react-hook-form";

import { Check } from "lucide-react";

import { Separator } from "@/components/ui/separator";

import { Badge } from "@/components/ui/badge";

import { ReferenceInput } from "@/components/admin/reference-input";

import { TextInput } from "@/components/admin/text-input";

import { SelectInput } from "@/components/admin/select-input";

import { ArrayInput } from "@/components/admin/array-input";

import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";

import { StatusSelector } from "../notes";

import type { Contact, Tag } from "../types";

import { Avatar } from "./Avatar";

import { AutocompleteCompanyInput } from "../companies/AutocompleteCompanyInput.tsx";

import { translatePersonalInfoTypeLabel } from "./contactModel.ts";

const CONTACT_TAG_GROUPS = [
  {
    key: "clients",
    title: "Клиенты и проектирование",
    names: ["Заказчик", "Дизайнер", "Архитектор"],
  },
  {
    key: "works",
    title: "Работы и подрядчики",
    names: [
      "Подрядчик",
      "Монтаж",
      "Доставка",
      "Логистика",
      "Замер",
      "Производство",
      "Столяр",
      "Мебельный цех",
      "Камень",
      "Стекло",
      "Металл",
    ],
  },
  {
    key: "suppliers",
    title: "Поставщики",
    names: [
      "Поставщик",
      "Фурнитура",
      "Материалы",
      "Оборудование",
      "Менеджер магазина",
    ],
  },
] as const;

const normalizeTagName = (value?: string | null) =>
  (value ?? "").trim().toLocaleLowerCase("ru-RU");

export const ContactInputs = () => {
  return (
    <div className="relative flex flex-col gap-6 p-1 md:static">
      <div className="absolute right-1 top-0 md:static">
        <Avatar />
      </div>

      <div className="flex flex-col gap-10 md:flex-row md:gap-8">
        <div className="flex flex-1 flex-col gap-8">
          <ContactIdentityInputs />

          <ContactPositionInputs />

          <ContactTagsInput />
        </div>

        <Separator
          orientation="vertical"
          className="hidden flex-shrink-0 md:block"
        />

        <div className="flex flex-1 flex-col gap-8">
          <ContactPersonalInformationInputs />

          <ContactNoteInput />
        </div>
      </div>
    </div>
  );
};

const ContactIdentityInputs = () => {
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">Основное</h6>

      <TextInput source="first_name" validate={required()} helperText={false} />

      <TextInput source="last_name" validate={required()} helperText={false} />
    </div>
  );
};

const ContactPositionInputs = () => {
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">Компания и должность</h6>

      <ReferenceInput source="company_id" reference="companies" perPage={25}>
        <AutocompleteCompanyInput label="resources.contacts.fields.company_id" />
      </ReferenceInput>

      <TextInput source="title" helperText={false} />
    </div>
  );
};

const ContactPersonalInformationInputs = () => {
  const translate = useTranslate();

  const { getValues, setValue } = useFormContext();

  const personalInfoTypes = [
    {
      id: "Work",
      name: translatePersonalInfoTypeLabel("Work", translate),
    },
    {
      id: "Home",
      name: translatePersonalInfoTypeLabel("Home", translate),
    },
    {
      id: "Other",
      name: translatePersonalInfoTypeLabel("Other", translate),
    },
  ];

  const handleEmailChange = (emailValue: string) => {
    const { first_name, last_name } = getValues();

    if (first_name || last_name || !emailValue) {
      return;
    }

    const [first, last] = emailValue.split("@")[0].split(".");

    if (!first) {
      return;
    }

    setValue("first_name", first.charAt(0).toUpperCase() + first.slice(1));

    setValue(
      "last_name",
      last ? last.charAt(0).toUpperCase() + last.slice(1) : "",
    );
  };

  const handleEmailPaste: ClipboardEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = (event) => {
    const value = event.clipboardData?.getData("text/plain");

    handleEmailChange(value);
  };

  const handleEmailBlur = (
    event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    handleEmailChange(event.target.value);
  };

  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">Контактные данные</h6>

      <div>
        <div className="mb-1 text-sm font-medium">Телефон</div>

        <ArrayInput source="phone_jsonb" helperText={false}>
          <SimpleFormIterator
            inline
            disableReordering
            disableClear
            className="[&>ul>li]:border-b-0 [&>ul>li]:pb-0"
          >
            <PhoneInput
              source="number"
              className="w-full"
              helperText={false}
              label={false}
              placeholder="+7..."
            />

            <SelectInput
              source="type"
              helperText={false}
              label={false}
              optionText="name"
              choices={personalInfoTypes}
              defaultValue="Work"
              className="w-28 min-w-28"
            />
          </SimpleFormIterator>
        </ArrayInput>
      </div>

      <div>
        <div className="mb-1 text-sm font-medium">Email</div>

        <ArrayInput source="email_jsonb" helperText={false}>
          <SimpleFormIterator
            inline
            disableReordering
            disableClear
            className="[&>ul>li]:border-b-0 [&>ul>li]:pb-0"
          >
            <TextInput
              source="email"
              className="w-full"
              helperText={false}
              label={false}
              placeholder="mail@example.com"
              validate={email()}
              onPaste={handleEmailPaste}
              onBlur={handleEmailBlur}
            />

            <SelectInput
              source="type"
              helperText={false}
              label={false}
              optionText="name"
              choices={personalInfoTypes}
              defaultValue="Work"
              className="w-28 min-w-28"
            />
          </SimpleFormIterator>
        </ArrayInput>
      </div>
    </div>
  );
};

const ContactTagsInput = () => {
  const { watch, setValue } = useFormContext<Contact>();

  const { data: tags = [] } = useGetList<Tag>("tags", {
    pagination: {
      page: 1,
      perPage: 100,
    },
    sort: {
      field: "name",
      order: "ASC",
    },
  });

  const selectedTags = (watch("tags") ?? []) as Identifier[];

  const toggleTag = (id: Identifier) => {
    const exists = selectedTags.includes(id);

    const next = exists
      ? selectedTags.filter((currentId) => currentId !== id)
      : [...selectedTags, id];

    setValue("tags", next as Contact["tags"], {
      shouldDirty: true,
    });
  };

  const groupedNames = new Set(
    CONTACT_TAG_GROUPS.flatMap((group) => group.names.map(normalizeTagName)),
  );

  const otherTags = tags.filter(
    (tag) => !groupedNames.has(normalizeTagName(tag.name)),
  );

  const renderTag = (tag: Tag) => {
    const selected = selectedTags.includes(tag.id);

    return (
      <button
        key={tag.id}
        type="button"
        onClick={() => toggleTag(tag.id)}
        className={[
          "rounded-full transition-all",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          selected
            ? "ring-2 ring-foreground/20"
            : "opacity-75 hover:opacity-100",
        ].join(" ")}
      >
        <Badge
          variant="secondary"
          className="cursor-pointer gap-1.5 px-2.5 py-1 text-sm font-normal rovero-colored-tag text-[#482719]"
          style={{
            backgroundColor: tag.color,
          }}
        >
          {selected && <Check className="h-3.5 w-3.5" />}

          {tag.name}
        </Badge>
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <CreateTagButton
        onCreated={(tag) =>
          setValue("tags", [...new Set([...(watch("tags") ?? []), tag.id])], {
            shouldDirty: true,
          })
        }
      />
      <div>
        <h6 className="text-lg font-semibold">Роли</h6>

        <p className="mt-1 text-sm text-muted-foreground">
          Один контакт может иметь несколько ролей
        </p>
      </div>

      {CONTACT_TAG_GROUPS.map((group) => {
        const groupTags = tags.filter((tag) =>
          group.names.some(
            (name) => normalizeTagName(name) === normalizeTagName(tag.name),
          ),
        );

        if (groupTags.length === 0) {
          return null;
        }

        return (
          <div key={group.key}>
            <div className="mb-2 text-xs font-medium text-muted-foreground">
              {group.title}
            </div>

            <div className="flex flex-wrap gap-2">
              {groupTags.map(renderTag)}
            </div>
          </div>
        );
      })}

      {otherTags.length > 0 && (
        <div>
          <div className="mb-2 text-xs font-medium text-muted-foreground">
            Другие
          </div>

          <div className="flex flex-wrap gap-2">{otherTags.map(renderTag)}</div>
        </div>
      )}

      {tags.length === 0 && (
        <div className="text-sm text-muted-foreground">
          Теги пока не созданы.
        </div>
      )}
    </div>
  );
};

const ContactNoteInput = () => {
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">Заметка</h6>

      <TextInput source="background" multiline helperText={false} />
    </div>
  );
};

/*
 * Пока оставляем экспорт,
 * потому что текущая карточка
 * контакта ещё использует его.
 *
 * На следующем шаге статус
 * из карточки уберём полностью.
 */
export const ContactStatusSelector = () => {
  const record = useRecordContext<Contact>();

  const [update] = useUpdate<Contact>();

  const notify = useNotify();

  if (!record) {
    return null;
  }

  const handleStatusChange = (nextStatus: string) => {
    if (nextStatus === record.status) {
      return;
    }

    update(
      "contacts",
      {
        id: record.id,
        data: {
          status: nextStatus,
        },
        previousData: record,
      },
      {
        mutationMode: "optimistic",

        onError: (error) => {
          notify(
            typeof error === "string"
              ? error
              : error?.message || "ra.notification.http_error",
            {
              type: "error",
              messageArgs: {
                _: typeof error === "string" ? error : error?.message,
              },
            },
          );
        },
      },
    );
  };

  return (
    <div className="[&_button]:w-auto">
      <StatusSelector
        status={record.status}
        setStatus={handleStatusChange}
        triggerClassName="w-full"
      />
    </div>
  );
};
